import {
  entryToIndicationSet,
  IndicationSet,
  RD_TYPE_INDICATION_SET,
} from 'features/indicationTriage'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { useComponentScope } from 'features/libra/workspace/ComponentScopeContext'
import { useRolodexEntriesWithSyncedRefreshes } from 'features/rolodex'
import sortBy from 'lodash/sortBy'
import { useMemo } from 'react'
import { GeckoComponent, GeckoEntry } from 'types'

import { resolveFfsArtifactIds } from './ffsIds'

interface UseFfsIndicationSetsArgs {
  component: GeckoComponent | undefined
  /**
   * When set, indication sets are filtered to those tagged with this
   * inspection. Untagged sets are kept (matches production
   * `useIndicationSets` semantics) unless `dropUntagged` is passed.
   */
  inspectionId: string | undefined
  /**
   * When `true`, drops untagged sets — used by validation flows that must
   * NOT be satisfied by legacy untagged sets. Default `false` matches the
   * production discovery semantics.
   */
  dropUntagged?: boolean
}

interface UseFfsIndicationSetsResult {
  indicationSets: ReadonlyArray<IndicationSet>
  isLoading: boolean
  forceRefresh: () => Promise<unknown>
}

/**
 * Shared indication-set discovery for FFS step components. Owns its query
 * (rather than going through `useIndicationSets`) so callers can compose
 * `forceRefresh` with their own refresh chains — generating a new LTA set
 * in binning, e.g., needs to invalidate the indication-set discovery used
 * by triage.
 *
 * Most callers want `useActiveLtaSet` (below) rather than this raw helper.
 */
export function useFfsIndicationSets({
  component,
  inspectionId,
  dropUntagged = false,
}: UseFfsIndicationSetsArgs): UseFfsIndicationSetsResult {
  const {
    entries: setEntries,
    isLoading,
    forceRefresh,
  } = useRolodexEntriesWithSyncedRefreshes(
    component?.tags
      ? { type: RD_TYPE_INDICATION_SET, tags: component.tags }
      : undefined
  )

  const indicationSets = useMemo<ReadonlyArray<IndicationSet>>(() => {
    return sortBy(
      (setEntries ?? [])
        .filter((entry) => {
          if (dropUntagged && !entry.tags.inspection) return false
          if (!inspectionId) return true
          if (!entry.tags.inspection) return true // untagged passes (production semantics)
          return entry.tags.inspection === inspectionId
        })
        .map((entry) => entryToIndicationSet(entry as GeckoEntry)),
      (is) => is.sortOrder
    )
  }, [setEntries, inspectionId, dropUntagged])

  return { indicationSets, isLoading, forceRefresh }
}

interface UseActiveLtaSetArgs {
  component: GeckoComponent | undefined
  inspectionId: string | undefined
}

interface UseActiveLtaSetResult {
  /** The active LTA indication set, matched by exact ticket-scoped id. */
  ltaSet: IndicationSet | undefined
  /** Every discovered indication set for the (component, inspection). */
  indicationSets: ReadonlyArray<IndicationSet>
  isLoading: boolean
  forceRefresh: () => Promise<unknown>
}

/**
 * Resolves the active LTA indication set for an FFS step. Single source of
 * truth for the binning + triage active-set lookup — both used to derive
 * the expected id separately and could drift on the id scheme.
 *
 * Asset tickets (a `ComponentScopeContext` is mounted) require the EXACT
 * ticket-scoped id from `resolveFfsArtifactIds`. Legacy linear tickets (no
 * scope) fall back to the unscoped id.
 */
export function useActiveLtaSet({
  component,
  inspectionId,
}: UseActiveLtaSetArgs): UseActiveLtaSetResult {
  const { ticketId } = useTicketAccess()
  const scope = useComponentScope()
  // `dropUntagged: true` for asset tickets — strict exact-id lookups must
  // not match untagged legacy sets. For the legacy path we also keep it
  // strict; an untagged set could only be discovered through the loose
  // production semantics and is not what binning/triage expect to operate
  // on.
  const { indicationSets, isLoading, forceRefresh } = useFfsIndicationSets({
    component,
    inspectionId,
    dropUntagged: true,
  })

  const ltaSet = useMemo<IndicationSet | undefined>(() => {
    if (!component || !inspectionId) return undefined
    const { ltaSetId } = resolveFfsArtifactIds({
      scoped: Boolean(scope),
      ticketId,
      componentId: component.id,
      unit: component.tags.unit,
      inspection: inspectionId,
    })
    return indicationSets.find((s) => s.id === ltaSetId)
  }, [indicationSets, component, inspectionId, scope, ticketId])

  return { ltaSet, indicationSets, isLoading, forceRefresh }
}
